import { z } from 'zod';
import {
    WidgetType,
    ZodBaseWidgetConfiguration,
    ZodGenericWidgetConfigurationDetails,
} from '../types';
import type { AutoTextResizerPropertyInterface } from '../../Utility/Vue/components/AutoTextResizer';
import type { ApiSuccessResponse } from '../../ReachApi';

export const ZodWeatherWidgetConfigurationDetails = ZodGenericWidgetConfigurationDetails.extend({
    css_temp: z.string(),
    css_day: z.string(),
    css_icon: z.string(),
    zip: z.string(),
    icon_version: z.string(),
    temperature_scale: z.string(),
});

export type WeatherWidgetConfigurationDetails = z.infer<
    typeof ZodWeatherWidgetConfigurationDetails
>;

export const ZodWeatherWidgetConfiguration = ZodBaseWidgetConfiguration.extend({
    type: z.literal(WidgetType.WEATHER),
    config: ZodWeatherWidgetConfigurationDetails,
});

export type WeatherWidgetConfiguration = z.infer<typeof ZodWeatherWidgetConfiguration>;

export const ZodForecastData = z.object({
    weather_locality_id: z.string().nullable(), // "Minneapolis, MN 55410, USA"
    area: z.string().nullish(), // "Minnesota"
    condition: z.string().nullable(), // "Mostly Cloudy"
    currday_background: z.string().nullable(), // "/images/weather_background/v1/mcloudy.png"
    currday_icon: z.string().nullable(), // "/images/weather_icon/v4/mcloudy.png"
    currday_temp: z.number().nullable(), // 22
    description: z.string().nullable(), // "Minneapolis, MN 55410, USA"
    feels_like: z.number().nullable(), // 22
    locality: z.string().nullable(), // "Minneapolis"
    today_condition: z.string().nullable(), // "Mostly Cloudy"
    today_hi: z.number().nullable(), // 27
    today_low: z.number().nullable(), // 19
    tomorrow_condition: z.string().nullable(), // "Mostly Cloudy"
    tomorrow_day: z.string().nullable(), // "Fri"
    tomorrow_feels_like: z.number().nullable(), // 14
    tomorrow_hi: z.number().nullable(), // 37
    tomorrow_icon: z.string().nullable(), // "/images/weather_icon/v4/mcloudy.png"
    tomorrow_low: z.number().nullable(), // 32
    day2_condition: z.string().nullable(),
    day2_day: z.string().nullable(),
    day2_feels_like: z.number().nullable(),
    day2_hi: z.number().nullable(),
    day2_icon: z.string().nullable(),
    day2_low: z.number().nullable(),
    day3_condition: z.string().nullable(),
    day3_day: z.string().nullable(),
    day3_feels_like: z.number().nullable(),
    day3_hi: z.number().nullable(),
    day3_icon: z.string().nullable(),
    day3_low: z.number().nullable(),
    day4_condition: z.string().nullable(),
    day4_day: z.string().nullable(),
    day4_feels_like: z.number().nullable(),
    day4_hi: z.number().nullable(),
    day4_icon: z.string().nullable(),
    day4_low: z.number().nullable(),
    day5_condition: z.string().nullable(),
    day5_day: z.string().nullable(),
    day5_feels_like: z.number().nullable(),
    day5_hi: z.number().nullable(),
    day5_icon: z.string().nullable(),
    day5_low: z.number().nullable(),
    day6_condition: z.string().nullable(),
    day6_day: z.string().nullable(),
    day6_feels_like: z.number().nullable(),
    day6_hi: z.number().nullable(),
    day6_icon: z.string().nullable(),
    day6_low: z.number().nullable(),
    wind_direction: z.number().nullable(), // 30
    wind_direction_icon: z.string().nullable(), // "/images/weather_icon/wind/wind_dir2.png"
    wind_speed: z.number().nullable(), // 1.73
});

export type ForecastData = z.infer<typeof ZodForecastData>;

export type HourlyWeatherData = {
    condition: string | null; //"Mostly Cloudy"
    date: string | null; // "03/04"
    day: string | null; //  "Fri"
    feels_like: number | null; //  14
    icon: string | null; // "/images/weather_icon/v4/mcloudy.png"
    temp_high: number | null; //  21
    temp_low: number | null; //  21
    time: string | null; //  "5am"
};

//should be moved when we convert api files to typescript.
export interface WeatherDataResponse extends ApiSuccessResponse {
    last_accessed: string;
    last_updated: string;
    next_update: string;
    version: string;
    weather: ApiWeatherData;
}

export type ApiWeatherData = {
    current: Array<WeatherCurrentLocationData>;
    forecast: Array<WeatherForecastData>;
    hourly: Array<WeatherHourlyForecastData>;
};

export type WeatherCurrentLocationData = {
    condition: string;
    current_generation: number;
    date: string;
    area: string;
    description: string;
    feels_like: number;
    icon: string;
    locality: string;
    temperature: number;
    timestamp: number;
    weather_locality_id: string;
    wind_direction: number;
    wind_speed: number;
};

export type WeatherForecastData = {
    weather_locality_id: string;
    current_generation: number;
    date: string;
    timestamp: number;
    temp_high: number | null;
    temp_low: number | null;
    condition: string;
    icon: string;
    wind_direction: number;
    wind_speed: number;
    feels_like: number;
};

export interface WeatherHourlyForecastData extends WeatherForecastData {
    time: string;
    utc: string;
}

export type WeatherWidgetTemplateData = {
    weather: ForecastData | null;
    hourly: Array<HourlyWeatherData>;
    lastUpdated?: number;

    //Used by Handlebars templates exclusively
    wid?: string;
    wobj?: {
        widget: {
            widgetsid: number;
            config: {
                css: string | null;
                css_day: string;
                css_temp: string;
                css_icon: string;
            };
        };
    };
    landscape?: boolean;
    portrait?: boolean;

    //Used for CSS only
    Widget?: {
        Id: number;
    };

    //Used by Vue templates exclusively
    widget: {
        id: number;
    };
    metadata: {
        landscape?: boolean;
        fontResize: boolean;
        fontProperties: AutoTextResizerPropertyInterface;
    };
};

export enum WeatherLayoutType {
    LANDSCAPE = 'landscape',
    PORTRAIT = 'portrait',
}
